import React, { useEffect, useState, useRef } from 'react';
import MapboxMap, { Marker, Popup, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useMapboxToken, handleMapboxError } from '../utils/mapbox';
import { locationsAPI } from '../api';
import { addressStreetLineOnly } from '../utils/addressUtils';

interface DeliveryMapProps {
  deliveryAddress: string;
  sellerLocation?: { lat: number; lng: number };
  status: string;
  orderDate: string;
  driverLocation?: { lat: number; lng: number };
  deliveryTimeOption?: string;
  /** Pre-geocoded delivery coords from checkout (used for accurate map pin) */
  deliveryCoords?: { lat: number; lng: number };
}

const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

const DeliveryMap: React.FC<DeliveryMapProps> = ({ 
  deliveryAddress, 
  sellerLocation,
  status,
  orderDate,
  driverLocation,
  deliveryTimeOption,
  deliveryCoords: deliveryCoordsProp
}) => {
  // Geocode delivery address to coordinates
  const { token: mapboxToken, loading: tokenLoading, valid: tokenValid } = useMapboxToken();
  const [deliveryCoords, setDeliveryCoords] = useState<[number, number] | null>(null);
  const [loading, setLoading] = useState(true);
  const [driverDistance, setDriverDistance] = useState<number | null>(null);
  const [isDriverNearby, setIsDriverNearby] = useState(false);
  const [driverPosition, setDriverPosition] = useState<[number, number] | null>(null);
  const [showDeliveryPopup, setShowDeliveryPopup] = useState(false);
  const [showSellerPopup, setShowSellerPopup] = useState(false);
  const [showDriverPopup, setShowDriverPopup] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  useEffect(() => {
    setGeocodeFailed(false);
    const trimmed = deliveryAddress.trim();
    if (!trimmed) {
      setGeocodeFailed(true);
      setLoading(false);
      return;
    }
    const run = async () => {
      try {
        const result = await locationsAPI.geocode(trimmed);
        if (result.lat != null && result.lon != null && Number.isFinite(result.lat) && Number.isFinite(result.lon)) {
          setDeliveryCoords([result.lat, result.lon]);
        } else {
          setGeocodeFailed(true);
        }
      } catch (error) {
        console.error('Geocoding error:', error);
        setGeocodeFailed(true);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [deliveryAddress]);

  // Animate driver position (only when no real driverLocation - use simulated progress)
  useEffect(() => {
    if (!deliveryCoords || !sellerLocation || (status !== 'shipped' && status !== 'processing') || driverLocation) {
      return;
    }

    const startPos: [number, number] = [sellerLocation.lat, sellerLocation.lng];
    const endPos: [number, number] = deliveryCoords;

    // Get max delivery hours based on option
    const getMaxDeliveryHours = (option?: string): number => {
      switch (option) {
        case '1-2':
          return 2;
        case '3-6':
          return 6;
        case '24':
          return 24;
        default:
          return 2; // Default to 2 hours
      }
    };

    const maxDeliveryHours = getMaxDeliveryHours(deliveryTimeOption);
    const estimatedDeliveryHours = maxDeliveryHours * 0.75; // Use 75% of max for progress calculation
    
    const startTime = new Date(orderDate).getTime();
    const now = Date.now();
    const elapsedHours = (now - startTime) / (1000 * 60 * 60);
    
    // Estimate progress based on time elapsed and delivery time option
    let progress = Math.min(elapsedHours / estimatedDeliveryHours, 0.95); // Cap at 95% until delivered

    const interval = setInterval(() => {
      // Gradually move towards delivery address
      progress += 0.005;
      if (progress >= 1) {
        progress = 0.99; // Keep at 99% until status changes to delivered
      }
      
      const lat = startPos[0] + (endPos[0] - startPos[0]) * progress;
      const lng = startPos[1] + (endPos[1] - startPos[1]) * progress;
      setDriverPosition([lat, lng]);
      
      // Calculate distance to delivery address
      const distance = calculateDistance(lat, lng, endPos[0], endPos[1]);
      setDriverDistance(distance);
      setIsDriverNearby(distance < 0.5); // Driver is nearby if within 0.5 miles
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [deliveryCoords, sellerLocation, status, orderDate, deliveryTimeOption, driverLocation]);

  // Use real driver location when provided (from API)
  useEffect(() => {
    if (deliveryCoords && driverLocation) {
      const distance = calculateDistance(
        driverLocation.lat,
        driverLocation.lng,
        deliveryCoords[0],
        deliveryCoords[1]
      );
      setDriverDistance(distance);
      setIsDriverNearby(distance < 0.5);
      setDriverPosition([driverLocation.lat, driverLocation.lng]);
    }
  }, [deliveryCoords, driverLocation]);

  if (loading || tokenLoading || !deliveryCoords) {
    return (
      <div className="w-full h-56 sm:h-72 md:h-96 bg-gray-200 rounded-lg flex items-center justify-center">
        <div className="text-center">
          {geocodeFailed ? (
            <>
              <i className="ri-map-pin-line text-3xl text-gray-400 mb-2 block"></i>
              <p className="text-gray-600">Could not locate this address</p>
            </>
          ) : (
            <>
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
              <p className="mt-2 text-gray-600">Loading map...</p>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!tokenValid || !mapboxToken) {
    return (
      <div className="w-full h-56 sm:h-72 md:h-96 rounded-lg bg-gray-200 flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-red-600 font-semibold">Mapbox token not configured</p>
          <p className="text-sm text-gray-600 mt-2">
            Set MAPBOX_ACCESS_TOKEN on the server or REACT_APP_MAPBOX_TOKEN in client/.env
          </p>
        </div>
      </div>
    );
  }

  const center: [number, number] = deliveryCoords;

  // Create route line GeoJSON: driver to delivery address when order is in transit
  const routeGeoJson = (status === 'shipped' || status === 'processing') && driverPosition ? {
    type: 'Feature' as const,
    geometry: {
      type: 'LineString' as const,
      coordinates: [
        [driverPosition[1], driverPosition[0]],
        [deliveryCoords[1], deliveryCoords[0]]
      ]
    },
    properties: {}
  } : null;

  return (
    <div className="w-full h-56 sm:h-72 md:h-96 rounded-lg overflow-hidden shadow-lg border-2 border-gray-200 relative">
      {mapError && (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center z-50">
          <div className="text-center p-4">
            <p className="text-red-600 font-semibold mb-2">Map failed to load</p>
            <p className="text-sm text-gray-600">{mapError}</p>
            <p className="text-xs text-gray-500 mt-2">Please check your Mapbox token</p>
          </div>
        </div>
      )}
      <MapboxMap
        mapboxAccessToken={mapboxToken}
        initialViewState={{
          longitude: center[1],
          latitude: center[0],
          zoom: 13
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle="mapbox://styles/mapbox/streets-v12"
        scrollZoom={true}
        onError={(e) => {
          handleMapboxError(e, setMapError);
        }}
        onLoad={() => {
          setMapError(null);
        }}
      >
        {/* Route line */}
        {routeGeoJson && (
          <Source id="route" type="geojson" data={routeGeoJson}>
            <Layer
              id="route-line"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
              paint={{
                'line-color': '#22C55E',
                'line-width': 4,
                'line-opacity': 0.6,
                'line-dasharray': [2, 2]
              }}
            />
          </Source>
        )}

        {/* Delivery Address Marker - deliveryCoords is [lat, lng] */}
        <Marker
          longitude={deliveryCoords[1]}
          latitude={deliveryCoords[0]}
          anchor="bottom"
        >
          <div
            className="cursor-pointer"
            onClick={() => setShowDeliveryPopup(true)}
          >
            <div className="bg-red-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg">
              📍
            </div>
          </div>
        </Marker>

        {showDeliveryPopup && (
          <Popup
            longitude={deliveryCoords[1]}
            latitude={deliveryCoords[0]}
            anchor="bottom"
            onClose={() => setShowDeliveryPopup(false)}
            closeButton={true}
            closeOnClick={false}
          >
            <div className="text-center">
              <p className="font-semibold">📍 Delivery Address</p>
              <p className="text-sm text-gray-600">
                {addressStreetLineOnly(null, deliveryAddress)}
              </p>
            </div>
          </Popup>
        )}

        {/* Seller Location Marker - only when we have real coordinates and order not yet in transit */}
        {sellerLocation && (status === 'pending' || status === 'processing') && (
          <>
            <Marker
              longitude={sellerLocation.lng}
              latitude={sellerLocation.lat}
              anchor="bottom"
            >
              <div
                className="cursor-pointer"
                onClick={() => setShowSellerPopup(true)}
              >
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg">
                  🏪
                </div>
              </div>
            </Marker>
            {showSellerPopup && (
              <Popup
                longitude={sellerLocation.lng}
                latitude={sellerLocation.lat}
                anchor="bottom"
                onClose={() => setShowSellerPopup(false)}
                closeButton={true}
                closeOnClick={false}
              >
                <div className="text-center">
                  <p className="font-semibold flex items-center">
                    <i className="ri-store-line mr-1"></i>
                    Seller Location
                  </p>
                  <p className="text-sm text-gray-600">Order prepared here</p>
                </div>
              </Popup>
            )}
          </>
        )}

        {/* Animated Delivery Person Marker */}
        {(status === 'shipped' || status === 'processing') && driverPosition && (
          <>
            <Marker
              longitude={driverPosition[1]}
              latitude={driverPosition[0]}
              anchor="bottom"
            >
              <div
                className="cursor-pointer"
                onClick={() => setShowDriverPopup(true)}
              >
                <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg animate-pulse">
                  🚚
                </div>
              </div>
            </Marker>
            {showDriverPopup && (
              <Popup
                longitude={driverPosition[1]}
                latitude={driverPosition[0]}
                anchor="bottom"
                onClose={() => setShowDriverPopup(false)}
                closeButton={true}
                closeOnClick={false}
              >
                <div className="text-center">
                  <p className="font-semibold">🚚 Delivery in Progress</p>
                  <p className="text-sm text-gray-600">Your order is on the way!</p>
                </div>
              </Popup>
            )}
          </>
        )}
      </MapboxMap>
      
      {/* Driver Nearby Notification */}
      {isDriverNearby && driverDistance !== null && (
        <div className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 max-w-[calc(100%-1rem)] bg-green-500 text-white rounded-lg shadow-lg px-3 py-2 sm:p-4 z-30 animate-pulse">
          <div className="flex items-center gap-2">
            <span className="text-xl sm:text-2xl flex-shrink-0">🚚</span>
            <div className="min-w-0">
              <p className="font-bold text-sm sm:text-lg">Driver Nearby!</p>
              <p className="text-xs sm:text-sm">About {driverDistance < 0.1 ? 'less than 0.1' : driverDistance.toFixed(1)} miles away</p>
            </div>
          </div>
        </div>
      )}

      {/* Driver Distance Info */}
      {driverDistance !== null && !isDriverNearby && (status === 'shipped' || status === 'processing') && (
        <div className="absolute top-2 sm:top-4 left-1/2 -translate-x-1/2 max-w-[calc(100%-1rem)] bg-green-500 text-white rounded-lg shadow-lg px-3 py-2 sm:p-3 z-30">
          <div className="flex items-center gap-2">
            <span className="text-lg sm:text-xl flex-shrink-0">🚚</span>
            <div className="min-w-0">
              <p className="font-semibold text-xs sm:text-sm">Driver Location</p>
              <p className="text-xs">About {driverDistance.toFixed(1)} miles away</p>
            </div>
          </div>
        </div>
      )}

      {/* Map Legend */}
      <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-white rounded-lg shadow-lg p-2 sm:p-3 z-20 max-w-[calc(100%-1rem)]">
        <div className="space-y-1 sm:space-y-2 text-xs sm:text-sm">
          <div className="flex items-center space-x-2">
            <span className="text-red-500">📍</span>
            <span>Delivery Address</span>
          </div>
          {(status === 'pending' || status === 'processing' || status === 'shipped') && sellerLocation && (
            <div className="flex items-center space-x-2">
              <i className="ri-store-line text-green-500"></i>
              <span>Seller Location</span>
            </div>
          )}
          {(status === 'shipped' || status === 'processing') && (
            <div className="flex items-center space-x-2">
              <span className="text-green-500">🚚</span>
              <span>Delivery in Progress</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryMap;
